import { Component } from '@angular/core';
import { TextEditorComponent } from '../../components/text-editor/text-editor.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  EditSystemInformationRequest,
  GetSystemInformationRequest,
  SystemInformationModel,
  SystemInformationsLanguageCodes,
} from '../../../core/models/systemInformation/system-information.model';
import { SystemInformationService } from '../../../core/services/systemInformation/system-information.service';
import { ToastService } from '../../../core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-information-setup',
  standalone: true,
  imports: [
    TextEditorComponent,
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
  ],
  templateUrl: './information-setup.component.html',
  styleUrl: './information-setup.component.scss',
})
export class InformationSetupComponent {
  constructor(
    private readonly systemInformationService: SystemInformationService,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService
  ) {
    this.getSystemInformation();
  }

  languageCode: SystemInformationsLanguageCodes = 'en';
  loading!: boolean;
  textEditorValue!: string;
  languageResponses: {
    [key in SystemInformationsLanguageCodes]?: SystemInformationModel;
  } = {};
  languages: {
    code: SystemInformationsLanguageCodes;
    name: string;
  }[] = [
    { code: 'en', name: this.translate.instant('lang.english') },
    { code: 'nl', name: this.translate.instant('lang.dutch') },
    { code: 'tr', name: this.translate.instant('lang.turkish') },
    { code: 'fr', name: this.translate.instant('lang.french') },
    { code: 'de', name: this.translate.instant('lang.german') },
    { code: 'it', name: this.translate.instant('lang.italian') },
    { code: 'es', name: this.translate.instant('lang.spanish') },
    { code: 'da', name: this.translate.instant('lang.danish') },
    { code: 'sv', name: this.translate.instant('lang.swedish') },
    { code: 'no', name: this.translate.instant('lang.norwegian') },
    { code: 'fi', name: this.translate.instant('lang.finnish') },
  ];

  getSystemInformation() {
    this.loading = true;
    const request: GetSystemInformationRequest = {
      languageCode: this.languageCode,
    };
    this.systemInformationService.getSystemInformation(request).subscribe({
      next: (res) => {
        this.loading = false;
        if (!res?.id) return;
        this.setResponseByLanguage(res);
        this.setTextEditorValue();
      },
    });
  }

  saveTextEditor() {
    if (!this.textEditorValue || this.textEditorValue == '<p></p>')
      return this.toastService.showToast(
        this.translate.instant('textEditorCannotBeEmpty'),
        'warning'
      );

    const id = this.getResponseIdByLanguage();
    const request: EditSystemInformationRequest = {
      id,
      information: this.textEditorValue,
      languageCode: this.languageCode,
    };
    this.loading = true;
    this.systemInformationService.editSystemInformation(request).subscribe({
      next: () => {
        this.getSystemInformation();
        this.toastService.showToast(
          this.translate.instant('processSuccessfull'),
          'success'
        );
      },
    });
  }

  changeLanguage(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.languageCode = selectElement.value as SystemInformationsLanguageCodes;
    this.setTextEditorValueByLanguage();
  }

  setTextEditorValue() {
    this.textEditorValue = JSON.parse(
      JSON.stringify(this.getResponseByLanguageCode().information)
    );
  }

  private setResponseByLanguage(res: SystemInformationModel) {
    this.languageResponses[this.languageCode] = res;
  }

  private getResponseByLanguageCode(): SystemInformationModel {
    return this.languageResponses[this.languageCode] as SystemInformationModel;
  }

  private getResponseIdByLanguage(): string {
    return this.getResponseByLanguageCode().id || '';
  }

  private setTextEditorValueByLanguage() {
    if (this.getResponseByLanguageCode()?.id) {
      this.setTextEditorValue();
    } else {
      this.getSystemInformation();
    }
  }
}
